import type { BuyableItemType } from "@prisma/client";
import { Prisma } from "@prisma/client";
import { Paginated, Sortable } from "./utils.types";

export const simpleOrganiserTypeValues =
  Prisma.validator<Prisma.OrganiserDefaultArgs>()({
    include: {
      structures: true,
    },
  });

export type SimpleOrganiserType = Prisma.OrganiserGetPayload<
  typeof simpleOrganiserTypeValues
>;

export const organiserStructureTypeValues =
  Prisma.validator<Prisma.OrganiserStructureDefaultArgs>()({
    include: {
      structure: {
        include: {
          address: true,
          socials: true,
          admin: true,
          picture: true,
          businessSectorRelation: true,
          eventStructures: {
            select: {
              stand: true,
              hall: true,
              eventId: true,
            },
          },
        },
      },
    },
  });

export type OrganiserStructureType = Prisma.OrganiserStructureGetPayload<
  typeof organiserStructureTypeValues
>;

export const organiserStructureWithProductsTypeValues =
  Prisma.validator<Prisma.OrganiserStructureDefaultArgs>()({
    include: {
      structure: {
        include: {
          address: true,
          socials: true,
          admin: true,
          picture: true,
          businessSectorRelation: true,
          products: {
            select: {
              id: true,
            },
          },
          eventStructures: {
            select: {
              exhibitorProfilesPack: true,
              eventId: true,
              stand: true,
              hall: true,
              promotionDateRanges: {
                select: {
                  from: true,
                  to: true,
                },
              },
            },
          },
        },
      },
    },
  });

export type OrganiserStructureWithProductsType =
  Prisma.OrganiserStructureGetPayload<
    typeof organiserStructureWithProductsTypeValues
  >;

export type PromotionDateRange = {
  from: string;
  to: string;
};

export type OrganiserStructureWithProductsTypeFormatted = Omit<
  OrganiserStructureWithProductsType,
  "structure"
> & {
  structure: Omit<
    OrganiserStructureWithProductsType["structure"],
    "eventStructures"
  > & {
    eventStructures: Array<
      Omit<
        OrganiserStructureWithProductsType["structure"]["eventStructures"][number],
        "promotionDateRanges"
      > & {
        promotionDateRanges: PromotionDateRange[];
      }
    >;
  };
};

export const formatOrganiserStructureWithProducts = (
  structure: OrganiserStructureWithProductsType
): OrganiserStructureWithProductsTypeFormatted => {
  return {
    ...structure,
    structure: {
      ...structure.structure,
      eventStructures: structure.structure.eventStructures.map(
        (eventStructure) => ({
          ...eventStructure,
          promotionDateRanges: eventStructure.promotionDateRanges.map(
            (range) => ({
              from: range.from,
              to: range.to,
            })
          ),
        })
      ),
    },
  };
};

type EventStructureFormatted = Omit<
  OrganiserStructureWithProductsType["structure"]["eventStructures"][number],
  "promotionDateRanges"
> & {
  promotionDateRanges: PromotionDateRange[];
};

export type OrganiserStructureWithProductsTypeFormattedForEvent = Omit<
  OrganiserStructureWithProductsTypeFormatted,
  "structure"
> & {
  structure: Omit<
    OrganiserStructureWithProductsTypeFormatted["structure"],
    "eventStructures"
  > & {
    eventStructure: EventStructureFormatted | null;
  };
};

export const formatOrganiserStructureWithProductsForEvent = (
  structure: OrganiserStructureWithProductsType,
  eventId: string
): OrganiserStructureWithProductsTypeFormattedForEvent => {
  const formattedStructure = formatOrganiserStructureWithProducts(structure);
  const eventStructure =
    formattedStructure.structure.eventStructures.find(
      (es) => es.eventId === eventId
    ) ?? null;

  return {
    ...formattedStructure,
    structure: {
      ...formattedStructure.structure,
      eventStructure,
    },
  };
};

export const organiserAdminTypeValues =
  Prisma.validator<Prisma.OrganiserDefaultArgs>()({
    include: {
      admin: true,
    },
  });

export type OrganiserAdminType = Prisma.OrganiserGetPayload<
  typeof organiserAdminTypeValues
>;

export type OrganiserSuggestionType = {
  value: string;
  label: string;
  phoneNumber?: string;
  email?: string;
};

export type OrganiserWithEvents = {
  id: string;
  name: string;
};

export const organiserWithCreditsTypeValues =
  Prisma.validator<Prisma.OrganiserDefaultArgs>()({
    select: {
      id: true,
      exhibitorProfilesPack1Credits: true,
      exhibitorProfilesPack2Credits: true,
      exhibitorProfilesPack3Credits: true,
    },
  });

export type OrganiserWithCreditsType = Prisma.OrganiserGetPayload<
  typeof organiserWithCreditsTypeValues
>;

export type OrganiserCredits = {
  exhibitorProfilesPack1Credits: number;
  exhibitorProfilesPack2Credits: number;
  exhibitorProfilesPack3Credits: number;
};

export type PendingPrestationItem = {
  buyableType: BuyableItemType;
  eventId: string | null;
};

export type PendingPrestationOrdersResponse = {
  hasPending: boolean;
  items: PendingPrestationItem[];
};

export type EventStructuresFilters = Partial<
  Sortable<
    Paginated<{
      searchText?: string;
    }>
  >
>;
